import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { api } from "../services/api";
import { useApiData } from "../hooks/useApiData";
import { useSession } from "../context/SessionContext";
import { DataState } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import DataTable, { TwoLine } from "../components/DataTable";
import StatusChip from "../components/StatusChip";
import { RISK_LEVEL, RISK_STATUS } from "../constants";
import { tint } from "../utils/colors";
import { formatDate } from "../utils/format";

function levelFor(score) {
  if (score >= 20) return "CRITICAL";
  if (score >= 12) return "HIGH";
  if (score >= 6) return "MEDIUM";
  return "LOW";
}

/** 5x5 matrix: likelihood across the bottom, impact up the side. Each cell shows how many open risks sit there. */
function Heatmap({ risks, selected, onSelect }) {
  const open = risks.filter((r) => r.status !== "CLOSED");
  const at = (likelihood, impact) => open.filter((r) => r.likelihood === likelihood && r.impact === impact);
  return (
    <SectionCard title="Risk heatmap" subtitle="Open risks by likelihood and impact. Click a cell to filter.">
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "stretch" }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ writingMode: "vertical-rl", transform: "rotate(180deg)", textAlign: "center", fontWeight: 700 }}
        >
          IMPACT →
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-around", pb: 4 }}>
          {[5, 4, 3, 2, 1].map((n) => (
            <Typography key={n} variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{n}</Typography>
          ))}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 0.75 }}>
            {[5, 4, 3, 2, 1].flatMap((impact) =>
              [1, 2, 3, 4, 5].map((likelihood) => {
                const level = levelFor(likelihood * impact);
                const color = RISK_LEVEL[level].color;
                const count = at(likelihood, impact).length;
                const isSelected = selected && selected.l === likelihood && selected.i === impact;
                return (
                  <Box
                    key={`${likelihood}-${impact}`}
                    onClick={() => onSelect(isSelected ? null : { l: likelihood, i: impact })}
                    title={`Likelihood ${likelihood}, impact ${impact}: ${count} open risk${count === 1 ? "" : "s"}`}
                    sx={{
                      aspectRatio: "1.25",
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                      fontWeight: 800,
                      fontSize: 18,
                      color: count ? "#fff" : color,
                      backgroundColor: count ? color : tint(color, 0.16),
                      outline: isSelected ? "3px solid #0F172A" : "none",
                      transition: "transform .15s ease",
                      "&:hover": { transform: "scale(1.05)" },
                    }}
                  >
                    {count || ""}
                  </Box>
                );
              })
            )}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", mt: 0.75, textAlign: "center" }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <Typography key={n} variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{n}</Typography>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 0.5, fontWeight: 700 }}>
            LIKELIHOOD →
          </Typography>
        </Box>
      </Box>
    </SectionCard>
  );
}

export default function RisksPage() {
  const { clientId } = useSession();
  const [cell, setCell] = useState(null);
  const state = useApiData(() => api.getRisks(), [clientId]);

  const columns = [
    {
      key: "score", label: "Score", width: 90,
      render: (r) => (
        <Box
          sx={{
            width: 42, height: 42, borderRadius: 2.5, display: "grid", placeItems: "center",
            fontWeight: 800, color: "#fff", backgroundColor: RISK_LEVEL[r.level].color,
          }}
          title={`${r.levelLabel} risk`}
        >
          {r.score}
        </Box>
      ),
    },
    { key: "title", label: "Risk", render: (r) => <TwoLine main={r.title} sub={`${r.category} · ${r.levelLabel}${r.applicationName ? ` · ${r.applicationName}` : ""}`} /> },
    { key: "owner", label: "Owner" },
    { key: "treatment", label: "Treatment" },
    { key: "status", label: "Status", render: (r) => <StatusChip label={r.statusLabel} color={RISK_STATUS[r.status].color} /> },
    { key: "dueDate", label: "Due", render: (r) => formatDate(r.dueDate) },
  ];

  return (
    <>
      <PageHeader title="Risk Register" subtitle="Score = likelihood × impact. 20+ is critical, 12+ high, 6+ medium." />
      <DataState state={state}>
        {(risks) => {
          const rows = cell ? risks.filter((r) => r.likelihood === cell.l && r.impact === cell.i && r.status !== "CLOSED") : risks;
          return (
            <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", lg: "minmax(340px, 1fr) 2fr" }, alignItems: "start" }}>
              <Heatmap risks={risks} selected={cell} onSelect={setCell} />
              <SectionCard title={cell ? `Likelihood ${cell.l} · Impact ${cell.i}` : "All risks"} subtitle="Highest score first">
                <DataTable columns={columns} rows={rows} emptyText="No risks in this cell." />
              </SectionCard>
            </Box>
          );
        }}
      </DataState>
    </>
  );
}
